import React from 'react';
import UseCasePageTemplate from '../../../components/UseCasePageTemplate';
import DataMeshAnimation from '../../../components/DataMeshAnimation';

const DataMeshPage = () => {
  // Data mesh icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  // Icons for solutions
  const catalogIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  );

  const governanceIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  );

  const lineageIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  // Icons for benefits
  const autonomyIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  const innovationIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  const scaleIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );

  // Icon for related use cases
  const qualityIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const dashboardIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );

  const challenges = [
    {
      title: "Centralized Data Teams Are Bottlenecks",
      description: "Traditional data architectures create bottlenecks where a small central team becomes responsible for serving all data needs across a growing organization."
    },
    {
      title: "Lack of Domain Context",
      description: "Data engineers often lack the business context that domain experts possess, leading to misalignment between data products and actual business needs."
    },
    {
      title: "Data Quality Concerns in Decentralized Models",
      description: "Organizations fear that decentralized data ownership will lead to inconsistent quality standards and decreased trust in data."
    },
    {
      title: "Complex Self-Service Implementation",
      description: "Enabling true self-service data access requires sophisticated governance, discovery, and quality monitoring capabilities that can be difficult to implement."
    }
  ];

  const solutions = [
    {
      icon: catalogIcon,
      title: "Comprehensive Data Discovery and Catalog",
      description: "Empower domain teams and data consumers with a rich, searchable data catalog that makes finding and understanding data assets intuitive.",
      items: [
        "Automated data asset discovery across all platforms",
        "Rich metadata management with business and technical context",
        "Powerful search functionality with relevance ranking",
        "Usage analytics to highlight popular and valuable assets",
        "Automated documentation and data dictionary creation"
      ]
    },
    {
      icon: governanceIcon,
      title: "Decentralized Data Quality Framework",
      description: "Provide domain teams with the tools they need to monitor, validate, and maintain the quality of the data products they own.",
      items: [
        "Customizable quality metrics for each data domain",
        "Self-service monitoring setup with templates",
        "Delegated quality oversight to domain experts",
        "Standardized quality SLAs with automated tracking",
        "Cross-domain quality visibility and benchmarking"
      ]
    },
    {
      icon: lineageIcon,
      title: "Domain-to-Domain Lineage and Impact Analysis",
      description: "Maintain visibility across domain boundaries with end-to-end lineage that shows how data flows between teams and products.",
      items: [
        "Visual mapping of cross-domain data dependencies",
        "Impact analysis for changes to shared data assets",
        "Ownership boundaries clearly defined in lineage view",
        "Data contract tracking between domains",
        "Lineage-based communication channels for data producers and consumers"
      ]
    }
  ];

  const benefits = [
    {
      icon: autonomyIcon,
      title: "Domain Team Autonomy",
      description: "Empower domain teams to independently create, manage and share their data products without constant dependency on central data teams."
    },
    {
      icon: innovationIcon,
      title: "Accelerated Innovation",
      description: "Enable faster development of data products and insights by reducing bottlenecks and putting data capabilities in the hands of domain experts."
    },
    {
      icon: scaleIcon,
      title: "Scalable Data Organization",
      description: "Create a data organization that can scale with your business growth by distributing responsibilities across domains while maintaining quality and governance."
    }
  ];

  const relatedUsesCases = [
    {
      icon: qualityIcon,
      title: "Data quality monitoring and testing",
      description: "Deploy and manage monitors and tests on one platform.",
      href: "/use-cases/data-quality"
    },
    {
      icon: dashboardIcon,
      title: "Report and dashboard integrity",
      description: "Produce reliable data your company can trust.",
      href: "/use-cases/data-observability"
    }
  ];

  return (
    <UseCasePageTemplate
      title="Data Mesh and Self-Serve Data"
      description="Empower domain teams to become data producers while ensuring quality and governance with tools that facilitate discovery, monitoring, and cross-domain collaboration."
      heroAnimation={<DataMeshAnimation />}
      icon={icon}
      challenges={challenges}
      solutions={solutions}
      benefits={benefits}
      relatedUsesCases={relatedUsesCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">Supporting Your Data Mesh Journey</h3>
          <p className="mb-4">
            Transitioning to a data mesh architecture requires more than just organizational changes—it requires tooling that supports decentralized data ownership while maintaining quality and governance.
          </p>
          <p>
            Qupid provides the foundational capabilities needed for successful data mesh implementation: discoverability through a comprehensive catalog, quality through domain-oriented monitoring, and interoperability through lineage and impact analysis.
          </p>
        </div>
      }
    />
  );
};

export default DataMeshPage; 